import React, { useState, useEffect } from 'react';
import { Modal } from "../ui/modal";
import Swal from 'sweetalert2';
import { client } from '../../sdkAdapter';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import Badge from "../ui/badge/Badge";
import MultiSelectSearchable from '../form/MultiSelectSearchable';
import { countries as fallbackCountries } from '../../constants/countries';
import MediaPickerModal from '../common/MediaPickerModal';

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const getFullUrl = (url?: string) => {
    if (!url) return undefined;
    if (url.startsWith('http')) return url;
    const base = (import.meta.env.VITE_API_BASE || 'http://127.0.0.1:8000').replace(/\/$/, '');
    return `${base}/${url.replace(/^\//, '')}`;
};

const isServiceActive = (s: any) =>
    s.active === true || s.active === 1 || s.active === '1' ||
    s.is_active === true || s.is_active === 1 || s.is_active === '1';

/* ─── icons ───────────────────────────────────────────────────────────────── */

function TruckIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
        </svg>
    );
}

function AirplaneIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
    );
}

function ShipIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M3 15a4 4 0 004 4h9a5 5 0 004-4v-2.5a.5.5 0 00-.5-.5H16l-3-3h-2l-3 3H4.5a.5.5 0 00-.5.5V15z" />
        </svg>
    );
}

function BoxIcon() {
    return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
    );
}

function SpinnerIcon() {
    return (
        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
    );
}

/* ─── service type icon mapping ───────────────────────────────────────────── */

function serviceIcon(slug: string) {
    const s = (slug || '').toLowerCase();
    if (s.includes('air') || s.includes('plane') || s.includes('express')) return <AirplaneIcon />;
    if (s.includes('ship') || s.includes('sea') || s.includes('ocean') || s.includes('freight')) return <ShipIcon />;
    if (s.includes('box') || s.includes('package') || s.includes('parcel') || s.includes('standard')) return <BoxIcon />;
    return <TruckIcon />;
}

/* ─── empty form ──────────────────────────────────────────────────────────── */

const EMPTY_SERVICE_FORM = {
    name: '', label: '', description: '', slug: '',
    image_id: 0 as number | null,
    image_preview: '',
    is_active: true,
};

/* ─── Main component ──────────────────────────────────────────────────────── */

export default function SettingsShipping() {
    // ── Data ──
    const [shippingServices, setShippingServices] = useState<any[]>([]);
    const [flatRates, setFlatRates] = useState<any[]>([]);
    const [allCountries, setAllCountries] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // ── Modal ──
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalTab, setModalTab] = useState<'configure' | 'manage'>('configure');

    // ── Configure tab ──
    const [checkedServiceIds, setCheckedServiceIds] = useState<Set<number>>(new Set());
    const [configSaving, setConfigSaving] = useState(false);

    // ── Manage Services tab ──
    const [editingServiceId, setEditingServiceId] = useState<number | 'new' | null>(null);
    const [serviceForm, setServiceForm] = useState({ ...EMPTY_SERVICE_FORM });
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
    const [serviceSaving, setServiceSaving] = useState(false);

    // ── Flat Rate modal ──
    const [isFlatRateModalOpen, setIsFlatRateModalOpen] = useState(false);
    const [editingFlatRateId, setEditingFlatRateId] = useState<number | null>(null);
    const [flatRateForm, setFlatRateForm] = useState({
        rate: 0,
        min_weight: 0,
        max_weight: 0,
        min_value: 0,
        max_value: 0,
        countries: [] as string[],
        active: true,
    });

    /* ── fetch ── */
    const refreshData = async () => {
        setLoading(true);
        try {
            const [svcRes, frRes] = await Promise.all([
                client.getApiShippingShippingServices() as any,
                client.getApiShippingFlatRates() as any,
            ]);

            const svcData = svcRes.data?.data?.data || svcRes.data?.data || svcRes.data || [];
            setShippingServices(Array.isArray(svcData) ? svcData : []);

            const frData = frRes.data?.data?.data || frRes.data?.data || frRes.data || [];
            setFlatRates(Array.isArray(frData) ? frData : []);
        } catch (err) {
            console.error('Failed to fetch shipping data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCountries = async () => {
        try {
            const res = await client.getV1CountriesCountries() as any;
            let data = res.data?.data || res.data || [];
            if (!data || data.length === 0) data = fallbackCountries;
            const formatted = data.map((c: any) =>
                typeof c === 'string'
                    ? { value: c, text: c }
                    : { value: c.id || c.code || c.name || c.value, text: c.name || c.label || c.text || c }
            );
            setAllCountries(formatted);
        } catch {
            setAllCountries(fallbackCountries);
        }
    };

    useEffect(() => { refreshData(); fetchCountries(); }, []);

    /* ── open / close modal ── */
    const openModal = (tab: 'configure' | 'manage' = 'configure') => {
        setCheckedServiceIds(new Set(shippingServices.filter(isServiceActive).map((s: any) => s.id as number)));
        setEditingServiceId(null);
        setServiceForm({ ...EMPTY_SERVICE_FORM });
        setModalTab(tab);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingServiceId(null);
        setServiceForm({ ...EMPTY_SERVICE_FORM });
    };

    /* ── toggle service checkbox ── */
    const toggleService = (id: number) => {
        setCheckedServiceIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    /* ── save configuration ── */
    const handleSaveConfig = async () => {
        setConfigSaving(true);
        try {
            const promises: Promise<any>[] = [];
            for (const svc of shippingServices) {
                const wasActive = isServiceActive(svc);
                const nowActive = checkedServiceIds.has(svc.id);
                if (wasActive !== nowActive) {
                    promises.push(
                        (client as any).postApiShippingShippingServiceUpdate({
                            postApiShippingShippingServiceUpdateRequest: {
                                id: svc.id, name: svc.name, label: svc.label,
                                description: svc.description || '', slug: svc.slug,
                                active: nowActive,
                            },
                        })
                    );
                }
            }
            await Promise.all(promises);
            closeModal();
            await refreshData();
            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Configuration saved', showConfirmButton: false, timer: 2000, timerProgressBar: true });
        } catch {
            Swal.fire({ title: 'Error', text: 'Failed to save configuration', icon: 'error' });
        } finally {
            setConfigSaving(false);
        }
    };

    /* ── service form ── */
    const handleServiceFormChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setServiceForm((prev) => ({
            ...prev,
            [name]: name === 'is_active' ? value === '1' : value,
        }));
    };

    const handleEditService = async (item: any) => {
        setEditingServiceId(item.id);
        setServiceForm({
            name: item.name || '', label: item.label || '', description: item.description || '',
            slug: item.slug || '', image_id: item.image_id || 0,
            image_preview: item.image?.path || item.image?.url || '',
            is_active: isServiceActive(item),
        });
        try {
            const res = await (client as any).getApiShippingShippingServiceId({ id: item.id }) as any;
            let data = res.data?.data || res.data;
            if (Array.isArray(data)) data = data[0];
            if (data) {
                setServiceForm({
                    name: data.name || '', label: data.label || '', description: data.description || '',
                    slug: data.slug || '', image_id: data.image_id || 0,
                    image_preview: data.image?.path || data.image?.url || '',
                    is_active: data.active !== undefined
                        ? (typeof data.active === 'string' ? data.active === '1' : !!data.active)
                        : (data.is_active ?? true),
                });
            }
        } catch { /* fall back */ }
    };

    const handleSaveService = async () => {
        if (!serviceForm.name || !serviceForm.label) return;
        setServiceSaving(true);
        try {
            const payload: any = {
                name: serviceForm.name, label: serviceForm.label,
                description: serviceForm.description, slug: serviceForm.slug,
                active: serviceForm.is_active,
            };
            if (serviceForm.image_id) payload.image_id = serviceForm.image_id;

            if (editingServiceId && editingServiceId !== 'new') {
                await (client as any).postApiShippingShippingServiceUpdate({
                    postApiShippingShippingServiceUpdateRequest: { ...payload, id: editingServiceId },
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Service updated', showConfirmButton: false, timer: 2000, timerProgressBar: true });
            } else {
                await (client as any).postApiShippingShippingServiceCreate({
                    postApiShippingShippingServiceCreateRequest: payload,
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Service created', showConfirmButton: false, timer: 2000, timerProgressBar: true });
            }

            setEditingServiceId(null);
            setServiceForm({ ...EMPTY_SERVICE_FORM });
            await refreshData();
        } catch (err: any) {
            const msg = err?.response?.data?.messages?.message || 'Failed to save service';
            Swal.fire({ icon: 'error', title: 'Error', text: typeof msg === 'string' ? msg : 'An error occurred' });
        } finally {
            setServiceSaving(false);
        }
    };

    const handleDeleteService = async (id: number) => {
        const result = await Swal.fire({
            title: 'Delete this service?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });
        if (result.isConfirmed) {
            try {
                await (client as any).postApiShippingShippingServiceDelete({
                    postApiShippingShippingServiceDeleteRequest: { id },
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Service deleted', showConfirmButton: false, timer: 2000, timerProgressBar: true });
                if (editingServiceId === id) { setEditingServiceId(null); setServiceForm({ ...EMPTY_SERVICE_FORM }); }
                await refreshData();
            } catch {
                Swal.fire({ title: 'Error', text: 'Failed to delete service', icon: 'error' });
            }
        }
    };

    /* ── flat rate CRUD ── */
    const handleFlatRateInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFlatRateForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (name === 'active' ? value === '1' : Number(value))
        }));
    };

    const openFlatRateModal = (item?: any) => {
        if (item) {
            setEditingFlatRateId(item.id);
            setFlatRateForm({
                rate: item.rate || item.cost || 0,
                min_weight: item.min_weight || 0,
                max_weight: item.max_weight || 0,
                min_value: item.min_value || 0,
                max_value: item.max_value || 0,
                countries: Array.isArray(item.countries) ? item.countries : (item.countries ? Object.values(item.countries) : []),
                active: item.active !== undefined ? item.active : true,
            });
        } else {
            setEditingFlatRateId(null);
            setFlatRateForm({ rate: 0, min_weight: 0, max_weight: 0, min_value: 0, max_value: 0, countries: [], active: true });
        }
        setIsFlatRateModalOpen(true);
    };

    const handleFlatRateSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!flatRateForm.countries || flatRateForm.countries.length === 0) {
            Swal.fire({ title: 'Validation Error', text: 'At least one country must be selected', icon: 'warning' });
            return;
        }
        try {
            const payload: any = {
                rate: flatRateForm.rate,
                min_weight: flatRateForm.min_weight,
                max_weight: flatRateForm.max_weight,
                min_value: flatRateForm.min_value,
                max_value: flatRateForm.max_value,
                active: flatRateForm.active,
            };
            if (flatRateForm.countries.length > 0) payload.countries = flatRateForm.countries;

            if (editingFlatRateId) {
                await client.postApiShippingFlatRateUpdate({
                    postApiShippingFlatRateUpdateRequest: { ...payload, id: editingFlatRateId } as any,
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Shipping rate updated', showConfirmButton: false, timer: 2000, timerProgressBar: true });
            } else {
                await client.postApiShippingFlatRateCreate({
                    postApiShippingFlatRateCreateRequest: { ...payload } as any,
                });
                Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Shipping rate created', showConfirmButton: false, timer: 2000, timerProgressBar: true });
            }
            setIsFlatRateModalOpen(false);
            setEditingFlatRateId(null);
            setFlatRateForm({ rate: 0, min_weight: 0, max_weight: 0, min_value: 0, max_value: 0, countries: [], active: true });
            refreshData();
        } catch {
            Swal.fire({ title: 'Error', text: 'Failed to save shipping rate', icon: 'error' });
        }
    };

    const handleDeleteFlatRate = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?', text: "You won't be able to revert this!", icon: 'warning',
            showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        });
        if (result.isConfirmed) {
            try {
                await client.postApiShippingFlatRateDelete({ postApiShippingFlatRateDeleteRequest: { id } } as any);
                Swal.fire('Deleted!', 'Shipping rate has been deleted.', 'success');
                refreshData();
            } catch {
                Swal.fire({ title: 'Error', text: 'Failed to delete shipping rate', icon: 'error' });
            }
        }
    };

    /* ── derived data ── */
    const activeServices = shippingServices.filter(isServiceActive);
    const totalActive = activeServices.length;

    /* ── render ── */
    return (
        <>
            {/* ── Main Card ── */}
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Active Shipping</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Shipping services currently available to customers.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {loading && (
                            <span className="text-sm text-gray-400 flex items-center gap-1.5">
                                <SpinnerIcon /> Loading…
                            </span>
                        )}
                        <button
                            onClick={() => openModal('manage')}
                            className="px-3 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h7" />
                            </svg>
                            Manage Services
                        </button>
                        <button
                            onClick={() => openModal('configure')}
                            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm shadow-brand-500/20"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Configure
                        </button>
                    </div>
                </div>

                {/* Active Services — Card Grid */}
                <div className="p-5">
                    {loading && totalActive === 0 ? (
                        /* Skeleton */
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="h-24 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
                            ))}
                        </div>
                    ) : totalActive === 0 ? (
                        /* Empty state */
                        <div className="flex flex-col items-center justify-center py-14 text-center">
                            <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                                <TruckIcon />
                            </div>
                            <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">No active shipping services</p>
                            <p className="text-xs text-gray-400 mt-1 mb-4">Click <span className="font-medium">Configure</span> to enable shipping services for your customers.</p>
                            <button
                                onClick={() => openModal('configure')}
                                className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium shadow-sm shadow-brand-500/20"
                            >
                                Configure Now
                            </button>
                        </div>
                    ) : (
                        /* Service cards */
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                            {activeServices.map((svc) => (
                                <ShippingCard
                                    key={svc.id}
                                    name={svc.label || svc.name}
                                    icon={svc.image
                                        ? <img src={getFullUrl(svc.image.path || svc.image.url)} alt={svc.name} className="w-5 h-5 object-contain" />
                                        : serviceIcon(svc.slug)
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>

                {totalActive > 0 && (
                    <div className="px-5 pb-4 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                        <p className="text-xs text-gray-400">
                            {totalActive} shipping service{totalActive !== 1 ? 's' : ''} active
                        </p>
                    </div>
                )}
            </div>

            {/* ── Flat Rates Section ── */}
            <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm mt-6">
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Flat Rate Configuration</h3>
                        <p className="text-xs text-gray-500 mt-0.5">Set flat-rate shipping costs by country, weight, and order value.</p>
                    </div>
                    <button
                        onClick={() => openFlatRateModal()}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium shadow-sm shadow-brand-500/20"
                    >
                        Add Flat Rate
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-5 py-3 text-start">Rate</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Weight (Min-Max)</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Value (Min-Max)</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Countries</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {flatRates.length === 0 ? (
                                <TableRow><TableCell colSpan={6} className="px-5 py-4 text-center text-gray-500 italic">No flat rates found.</TableCell></TableRow>
                            ) : (
                                flatRates.map(item => (
                                    <TableRow key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.01]">
                                        <TableCell className="px-5 py-4 font-mono font-bold text-gray-700 dark:text-gray-200">{item.rate || item.cost}</TableCell>
                                        <TableCell className="px-5 py-4 text-sm text-gray-600 dark:text-gray-400 font-medium">{item.min_weight}kg - {item.max_weight}kg</TableCell>
                                        <TableCell className="px-5 py-4 text-sm text-gray-600 dark:text-gray-400 font-medium">{item.min_value} - {item.max_value}</TableCell>
                                        <TableCell className="px-5 py-4">
                                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                                                {(() => {
                                                    const countryCodes = Array.isArray(item.countries) ? item.countries : (item.countries ? Object.values(item.countries) : []);
                                                    if (countryCodes.length === 0) return <span className="text-gray-400 italic">None</span>;
                                                    return (
                                                        <div className="flex flex-wrap gap-1">
                                                            {countryCodes.slice(0, 2).map((code: any) => {
                                                                const countryName = allCountries.find((c: any) => c.value === code)?.text || code;
                                                                return (
                                                                    <span key={code} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-[10px] text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                                                                        {countryName}
                                                                    </span>
                                                                );
                                                            })}
                                                            {countryCodes.length > 2 && (
                                                                <span className="px-2 py-0.5 bg-brand-50 dark:bg-brand-500/10 rounded text-[10px] text-brand-600 dark:text-brand-400 border border-brand-100 dark:border-brand-500/20 font-bold">
                                                                    +{countryCodes.length - 2} more
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </TableCell>
                                        <TableCell className="px-5 py-4">
                                            <Badge color={item.active ? 'success' : 'error'} size="sm">
                                                {item.active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="px-5 py-4 text-end">
                                            <div className="flex justify-end gap-3">
                                                <button onClick={() => openFlatRateModal(item)} className="text-brand-500 hover:text-brand-700 transition-colors">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button onClick={() => handleDeleteFlatRate(item.id)} className="text-red-500 hover:text-red-700 transition-colors">
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* ── Flat Rate Modal ── */}
            <Modal isOpen={isFlatRateModalOpen} onClose={() => { setIsFlatRateModalOpen(false); setEditingFlatRateId(null); }} className="max-w-2xl p-0 overflow-hidden">
                <div className="flex flex-col h-full max-h-[90vh]">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-10">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">{editingFlatRateId ? 'Edit Flat Rate' : 'Add Flat Rate'}</h3>
                    </div>
                    <div className="p-6 overflow-y-auto flex-grow">
                        <form id="flat-rate-form" onSubmit={handleFlatRateSubmit} className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Rate Cost <span className="text-red-500">*</span></label>
                                    <input type="number" name="rate" step="0.01" min="0" required placeholder="0.00"
                                        value={flatRateForm.rate} onChange={handleFlatRateInputChange}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20 font-mono" />
                                </div>
                                <div className="space-y-1.5 flex-1">
                                    <label className="block text-sm font-semibold mb-1.5 text-gray-700 dark:text-gray-300">Status</label>
                                    <select name="active" value={flatRateForm.active ? '1' : '0'}
                                        onChange={handleFlatRateInputChange as any}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20">
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <h4 className="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">Geographic Constraints <span className="text-red-500">*</span></h4>
                                <MultiSelectSearchable label="Applicable Countries"
                                    options={allCountries} value={flatRateForm.countries}
                                    onChange={(selected) => setFlatRateForm(prev => ({ ...prev, countries: selected }))}
                                    placeholder="Select countries..." searchPlaceholder="Search countries..." />
                            </div>
                            <div className="p-4 bg-gray-50/50 dark:bg-white/[0.02] rounded-xl border border-gray-100 dark:border-gray-800 space-y-4">
                                <h4 className="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">Weight Constraints</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400 text-xs">Min Weight (kg)</label>
                                        <input type="number" name="min_weight" step="0.01" min="0"
                                            value={flatRateForm.min_weight} onChange={handleFlatRateInputChange}
                                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20 font-mono" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400 text-xs">Max Weight (kg)</label>
                                        <input type="number" name="max_weight" step="0.01" min="0"
                                            value={flatRateForm.max_weight} onChange={handleFlatRateInputChange}
                                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20 font-mono" />
                                    </div>
                                </div>
                            </div>
                            <div className="p-4 bg-gray-50/50 dark:bg-white/[0.02] rounded-xl border border-gray-100 dark:border-gray-800 space-y-4">
                                <h4 className="text-sm font-bold text-gray-800 dark:text-white uppercase tracking-wider">Order Value Constraints</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400 text-xs">Min Order Value</label>
                                        <input type="number" name="min_value" step="0.01" min="0"
                                            value={flatRateForm.min_value} onChange={handleFlatRateInputChange}
                                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20 font-mono" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400 text-xs">Max Order Value <span className="text-red-500">*</span></label>
                                        <input type="number" name="max_value" step="0.01" min="0" required
                                            value={flatRateForm.max_value} onChange={handleFlatRateInputChange}
                                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20 font-mono" />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] sticky bottom-0 z-10 flex justify-end gap-3">
                        <button type="button" onClick={() => { setIsFlatRateModalOpen(false); setEditingFlatRateId(null); }}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors">Cancel</button>
                        <button type="submit" form="flat-rate-form"
                            className="px-6 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 transition-all active:scale-95">
                            {editingFlatRateId ? 'Save Changes' : 'Add Flat Rate'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Unified Modal (Services) ── */}
            <Modal isOpen={isModalOpen} onClose={closeModal} className="max-w-2xl p-0 overflow-hidden">
                <div className="flex flex-col h-full max-h-[90vh]">
                    {/* Modal header + tabs */}
                    <div className="px-6 pt-6 pb-0 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-10">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">Shipping Options</h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {modalTab === 'configure'
                                ? 'Choose which shipping services are available to customers.'
                                : 'Add, edit, or delete shipping services in your registry.'}
                        </p>
                        <div className="flex gap-0 mt-4 -mb-px">
                            <ModalTab active={modalTab === 'configure'} onClick={() => setModalTab('configure')}>
                                Configure
                            </ModalTab>
                            <ModalTab active={modalTab === 'manage'} onClick={() => setModalTab('manage')}>
                                Manage Services
                                <span className={`ml-2 text-xs font-bold px-1.5 py-0.5 rounded-full ${modalTab === 'manage' ? 'bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}>
                                    {shippingServices.length}
                                </span>
                            </ModalTab>
                        </div>
                    </div>

                    {/* ── Configure Tab ── */}
                    {modalTab === 'configure' && (
                        <div className="p-6 overflow-y-auto flex-grow space-y-5">
                            <SectionCard
                                icon={<TruckIcon />}
                                title="Shipping Services"
                                description="Toggle which shipping services are active"
                                accent="brand"
                            >
                                {shippingServices.length === 0 ? (
                                    <p className="text-sm text-gray-400 italic">No shipping services available.</p>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {shippingServices.map((svc) => (
                                            <label
                                                key={svc.id}
                                                htmlFor={`cfg-svc-${svc.id}`}
                                                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all text-sm ${checkedServiceIds.has(svc.id)
                                                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`cfg-svc-${svc.id}`}
                                                    checked={checkedServiceIds.has(svc.id)}
                                                    onChange={() => toggleService(svc.id)}
                                                    className="w-4 h-4 accent-brand-500 shrink-0 rounded"
                                                />
                                                <span className="font-medium text-gray-700 dark:text-gray-200 truncate">{svc.label || svc.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </SectionCard>
                        </div>
                    )}

                    {/* ── Manage Services Tab ── */}
                    {modalTab === 'manage' && (
                        <div className="p-6 overflow-y-auto flex-grow space-y-4">
                            {/* Inline Add / Edit Form */}
                            {editingServiceId !== null && (
                                <div className="rounded-xl border border-brand-200 dark:border-brand-500/30 bg-brand-50/40 dark:bg-brand-500/5 overflow-hidden">
                                    <div className="px-4 py-3 border-b border-brand-200 dark:border-brand-500/30 flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-md bg-brand-100 dark:bg-brand-500/20 text-brand-600 dark:text-brand-400 flex items-center justify-center">
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                {editingServiceId === 'new'
                                                    ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                }
                                            </svg>
                                        </div>
                                        <p className="text-sm font-semibold text-gray-800 dark:text-white">
                                            {editingServiceId === 'new' ? 'Add New Service' : 'Edit Service'}
                                        </p>
                                    </div>
                                    <div className="p-4 space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Name (Internal) <span className="text-red-500">*</span></label>
                                                <input type="text" name="name" value={serviceForm.name}
                                                    onChange={handleServiceFormChange}
                                                    placeholder="e.g. standard_delivery"
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-colors" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Label (Display) <span className="text-red-500">*</span></label>
                                                <input type="text" name="label" value={serviceForm.label}
                                                    onChange={handleServiceFormChange}
                                                    placeholder="e.g. Standard Delivery"
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400 transition-colors" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Slug</label>
                                                <input type="text" name="slug" value={serviceForm.slug}
                                                    onChange={handleServiceFormChange}
                                                    placeholder="e.g. standard-delivery"
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 font-mono transition-colors" />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Status</label>
                                                <select name="is_active" value={serviceForm.is_active ? '1' : '0'}
                                                    onChange={handleServiceFormChange as any}
                                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 transition-colors">
                                                    <option value="1">Active</option>
                                                    <option value="0">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">Description</label>
                                            <textarea name="description" value={serviceForm.description}
                                                onChange={handleServiceFormChange} rows={2}
                                                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-gray-800 outline-none focus:ring-2 focus:ring-brand-500/20 resize-none transition-colors" />
                                        </div>
                                        {/* Image picker */}
                                        <div className="flex items-center gap-3">
                                            <div className="relative w-11 h-11 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-black/20 group shrink-0">
                                                {serviceForm.image_preview ? (
                                                    <>
                                                        <img src={getFullUrl(serviceForm.image_preview)} alt="Preview" className="w-full h-full object-cover" />
                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <button type="button"
                                                                onClick={() => setServiceForm((p) => ({ ...p, image_id: 0, image_preview: '' }))}
                                                                className="p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600">
                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                )}
                                            </div>
                                            <button type="button" onClick={() => setIsMediaModalOpen(true)}
                                                className="px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400">
                                                {serviceForm.image_preview ? 'Change Image' : 'Select Image'}
                                            </button>
                                        </div>
                                        {/* Form actions */}
                                        <div className="flex justify-end gap-2 pt-1">
                                            <button onClick={() => { setEditingServiceId(null); setServiceForm({ ...EMPTY_SERVICE_FORM }); }}
                                                className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors">Cancel</button>
                                            <button onClick={handleSaveService}
                                                disabled={serviceSaving || !serviceForm.name || !serviceForm.label}
                                                className="px-4 py-1.5 text-xs text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-sm shadow-brand-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                                                {serviceSaving ? <><SpinnerIcon />Saving…</> : (editingServiceId === 'new' ? 'Create Service' : 'Save Changes')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Services list */}
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-white/[0.02] flex items-center justify-between">
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">All Services</p>
                                    {editingServiceId === null && (
                                        <button onClick={() => { setEditingServiceId('new'); setServiceForm({ ...EMPTY_SERVICE_FORM }); }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-white rounded-lg hover:bg-brand-600 text-xs font-semibold transition-colors shadow-sm shadow-brand-500/20">
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            Add New Service
                                        </button>
                                    )}
                                </div>
                                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                    {shippingServices.length === 0 ? (
                                        <div className="py-10 text-center text-sm text-gray-400">
                                            {loading ? 'Loading…' : 'No shipping services found. Add one above.'}
                                        </div>
                                    ) : (
                                        shippingServices.map((item) => {
                                            const active = isServiceActive(item);
                                            const isBeingEdited = editingServiceId === item.id;
                                            return (
                                                <div key={item.id}
                                                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${isBeingEdited ? 'bg-brand-50/40 dark:bg-brand-500/5' : 'hover:bg-gray-50/60 dark:hover:bg-white/[0.01]'}`}>
                                                    {/* Icon */}
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400' : 'bg-gray-100 text-gray-400 dark:bg-gray-800'}`}>
                                                        {item.image
                                                            ? <img src={getFullUrl(item.image.path || item.image.url)} alt={item.name} className="w-4 h-4 object-contain" />
                                                            : serviceIcon(item.slug)
                                                        }
                                                    </div>
                                                    {/* Info */}
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{item.label || item.name}</p>
                                                        <p className="text-xs text-gray-400 font-mono truncate">{item.slug}</p>
                                                    </div>
                                                    {/* Status pill */}
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${active ? 'text-green-700 bg-green-100 dark:bg-green-500/20 dark:text-green-400' : 'text-gray-500 bg-gray-100 dark:bg-gray-800'}`}>
                                                        {active ? 'Active' : 'Inactive'}
                                                    </span>
                                                    {/* Actions */}
                                                    <div className="flex items-center gap-0.5 shrink-0">
                                                        <button onClick={() => handleEditService(item)}
                                                            className={`p-1.5 rounded-lg transition-colors ${isBeingEdited ? 'text-brand-500 bg-brand-100 dark:bg-brand-500/20' : 'text-gray-400 hover:text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-500/10'}`}
                                                            title="Edit">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button onClick={() => handleDeleteService(item.id)}
                                                            className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                                            title="Delete">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer */}
                    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] sticky bottom-0 z-10 flex justify-between items-center">
                        <button type="button" onClick={closeModal}
                            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors">
                            {modalTab === 'manage' ? 'Done' : 'Cancel'}
                        </button>
                        {modalTab === 'configure' && (
                            <button type="button" onClick={handleSaveConfig} disabled={configSaving}
                                className="px-6 py-2 text-sm text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                                {configSaving ? <><SpinnerIcon />Saving…</> : 'Save Configuration'}
                            </button>
                        )}
                    </div>
                </div>
            </Modal>

            {/* Media Picker */}
            <MediaPickerModal
                isOpen={isMediaModalOpen}
                onClose={() => setIsMediaModalOpen(false)}
                onSelect={(file) => {
                    setServiceForm((p) => ({ ...p, image_id: file.id, image_preview: file.path || file.url || '' }));
                    setIsMediaModalOpen(false);
                }}
            />
        </>
    );
}

/* ─── ShippingCard ────────────────────────────────────────────────────────── */

interface ShippingCardProps {
    name: string;
    icon: React.ReactNode;
}

function ShippingCard({ name, icon }: ShippingCardProps) {
    return (
        <div className="flex flex-col gap-2.5 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm transition-all group">
            <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
                    {icon}
                </div>
                <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 px-1.5 py-0.5 rounded-full">
                    Active
                </span>
            </div>
            <div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white truncate leading-tight">{name}</p>
                <p className="text-xs text-gray-400 mt-0.5">Service</p>
            </div>
        </div>
    );
}

/* ─── SectionCard ─────────────────────────────────────────────────────────── */

interface SectionCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    accent: 'brand' | 'gray';
    children: React.ReactNode;
}

function SectionCard({ icon, title, description, accent, children }: SectionCardProps) {
    const accentClass = accent === 'brand'
        ? 'bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-white/[0.02]">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${accentClass}`}>{icon}</div>
                <div>
                    <p className="font-semibold text-gray-800 dark:text-white text-sm">{title}</p>
                    <p className="text-xs text-gray-500">{description}</p>
                </div>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

/* ─── ModalTab ────────────────────────────────────────────────────────────── */

function ModalTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button onClick={onClick}
            className={`flex items-center px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${active ? 'border-brand-500 text-brand-600 dark:text-brand-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}>
            {children}
        </button>
    );
}
